import { useState, useEffect, useRef } from 'react';
import { Trans } from '@lingui/react/macro';
import { Link } from 'react-router';
import { motion, useScroll } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export default function PromoBanner({ onVisibilityChange, onHeightChange }: { onVisibilityChange?: (visible: boolean) => void; onHeightChange?: (height: number) => void }) {
  const [visible, setVisible] = useState(true);
  const { scrollY } = useScroll();
  const bannerRef = useRef<HTMLDivElement>(null);

  // Report actual height to parent
  useEffect(() => {
    if (!bannerRef.current || !onHeightChange) return;
    const observer = new ResizeObserver(([entry]) => {
      onHeightChange(entry.contentRect.height);
    });
    observer.observe(bannerRef.current);
    onHeightChange(bannerRef.current.offsetHeight);
    return () => observer.disconnect();
  }, [onHeightChange]);

  useEffect(() => {
    const unsubscribe = scrollY.on('change', (latest) => {
      const shouldShow = latest < 10;
      setVisible(shouldShow);
      onVisibilityChange?.(shouldShow);
    });
    return () => unsubscribe();
  }, [scrollY, onVisibilityChange]);

  return (
    <motion.div
      ref={bannerRef}
      className="fixed top-0 left-0 right-0 z-[60] bg-brand-dark-blue text-white"
      initial={{ y: 0 }}
      animate={{ y: visible ? 0 : -80 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ pointerEvents: visible ? 'auto' : 'none' }}
    >
      {/* Shimmer pill that shoots left to right */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-y-0 w-24 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_3s_ease-in-out_infinite] -translate-x-full" />
      </div>

      {/* Desktop: single line with quiz CTA */}
      <Link
        to="/quiz"
        className="relative hidden sm:flex items-center justify-center gap-2 px-4 py-2 text-base font-bold hover:bg-white/5 transition-colors"
      >
        <span>
          🚀 <Trans>Get 20% off interview practice</Trans>
        </span>
        <span>—</span>
        <span className="flex items-center gap-1.5 bg-white/20 px-3 py-0.5 rounded hover:bg-white/30 transition-colors">
          <Trans>Take the quiz</Trans>
          <ArrowRight className="w-4 h-4" />
        </span>
      </Link>

      {/* Mobile: marquee text + quiz button below */}
      <div className="relative sm:hidden flex flex-col items-center py-2 gap-2">
        {/* Scrolling text */}
        <div className="w-full overflow-hidden">
          <div className="flex w-max animate-scroll-infinite">
            {[0, 1].map((i) => (
              <span key={i} className="text-sm font-bold whitespace-nowrap px-8">
                🚀 <Trans>Get 20% off interview practice</Trans>
                <span className="mx-6 opacity-40">•</span>
                🚀 <Trans>Get 20% off interview practice</Trans>
                <span className="mx-6 opacity-40">•</span>
              </span>
            ))}
          </div>
        </div>
        {/* Quiz CTA */}
        <Link
          to="/quiz"
          className="flex items-center gap-1.5 text-xs font-bold bg-white/20 px-3 py-1 rounded hover:bg-white/30 transition-colors"
        >
          <Trans>Take the quiz</Trans>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </motion.div>
  );
}
